import React, { useState } from "react";
import { Link } from "react-router-dom";
import "../styles/join-form.css";
import Footer from "./Footer";
import { submitUserForm } from "../services/cognitoService";
import { Helmet } from "react-helmet";

interface JoinFormProps {
  customPath?: string; // Optional custom path (e.g., "join" or "username")
}

const JoinForm: React.FC<JoinFormProps> = ({ customPath = "" }) => {
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [twitterHandle, setTwitterHandle] = useState(""); // Twitter handle (alphanumeric)
  const [emailConsent, setEmailConsent] = useState(false);
  const [errors, setErrors] = useState<{
    username?: string;
    email?: string;
    emailConsent?: string;
    general?: string;
  }>({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const usernameRegex = /^[a-zA-Z0-9]+$/;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  const tweetContent = `🐾 I'm on @TheCuriosDog as ${
    username || "join"
  } to ask & answer anonymously! 🐕✨ Join me, claim your unique username, and let's get curious together! 🚀 https://curios.dog/${
    username || "join"
  } #CuriosDog`;

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsProcessing(true);

    const newErrors: {
      username?: string;
      email?: string;
      emailConsent?: string;
    } = {};

    // Convert all inputs to lowercase
    const lowerUsername = username.trim().toLowerCase();
    const lowerEmail = email.toLowerCase();
    const lowerTwitterHandle = twitterHandle.toLowerCase();

    // Validate username
    if (!usernameRegex.test(lowerUsername)) {
      newErrors.username = "Username must be alphanumeric with no spaces or special characters.";
    }

    // Validate email
    if (!emailRegex.test(lowerEmail)) {
      newErrors.email = "Please enter a valid email address.";
    }

    // Validate email consent
    if (!emailConsent) {
      newErrors.emailConsent =
        "You must consent to the creation of an account with Curios.dog.";
    }

    setErrors(newErrors);

    const generatePassword = (length: number = 12) => {
      const uppercase = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
      const lowercase = "abcdefghijklmnopqrstuvwxyz";
      const digits = "0123456789";
      const specialChars = "!@£$%^&*()_+[]{}|;:,.<>?";

      const requiredChars = [
        uppercase[Math.floor(Math.random() * uppercase.length)],
        lowercase[Math.floor(Math.random() * lowercase.length)],
        digits[Math.floor(Math.random() * digits.length)],
        specialChars[Math.floor(Math.random() * specialChars.length)],
      ];

      const allChars = uppercase + lowercase + digits + specialChars;
      const remainingChars = Array.from(
        { length: length - 4 },
        () => allChars[Math.floor(Math.random() * allChars.length)]
      );
      const passwordArray = [...requiredChars, ...remainingChars];
      return passwordArray.sort(() => Math.random() - 0.5).join("");
    };

    if (Object.keys(newErrors).length === 0) {
      try {
        const result = await submitUserForm({
          username: lowerUsername,
          email: lowerEmail,
          consent: emailConsent,
          twitterHandle: lowerTwitterHandle,
          password: generatePassword(12),
          inviteOrigin: customPath || lowerUsername,
        });

        if (result.success) {
          setIsSubmitted(true);
        }
      } catch (error: any) {
        const cleanMessage = error.message
          .replace("PreSignUp failed with error ", "")
          .trim();
        if (cleanMessage.includes("Username already exists")) {
          setErrors({
            general:
              "The username is already taken. Please choose another one.",
          });
        } else if (cleanMessage.includes("Email already exists")) {
          setErrors({ general: "An account with this email already exists." });
        } else {
          setErrors({ general: cleanMessage });
        }
      }
    }

    setIsProcessing(false);
  };

  if (isSubmitted) {
    return (
      <div className="thank-you-container">
        <div className="text-center mb-4">
          <Link to="/">
            <img
              src={`${process.env.PUBLIC_URL}/curios-dog.jpeg`}
              alt="Curious Dog"
              className="thank-you-image"
            />
          </Link>
          <h1 className="thank-you-title">Thank you, {username}!</h1>
          <p className="thank-you-message">
            We will let you know when your username is reserved.
          </p>
        </div>
        <div className="col-md-6 mx-auto">
          {/* Post to X Section */}
          <div className="mb-3">
            <label htmlFor="tweetContent" className="form-label">
              Your Tweet:
            </label>
            <textarea
              id="tweetContent"
              className="form-control"
              style={{ height: "130px" }}
              value={tweetContent}
              readOnly
            />
          </div>

          <a
            href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
              tweetContent
            )}`}
            className="btn btn-primary w-100 mb-3"
            target="_blank"
            rel="noopener noreferrer"
          >
            🐦 Post on X to start getting followers
          </a>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="join-page-container">
      <Helmet>
        <title>Curios Dog - Join the Fun, Ask and Answer Anonymously</title>
        <meta name="description" content="Curios Dog is a new anonymous Q&A platform for curious minds. Join, ask, and answer anonymously. Reserve your unique username today!" />
        <meta name="keywords" content="Curios Dog, Curious Cat alternative, anonymous Q&A, join the waiting list, anonymous questions, ask anonymously, reserve username" />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://curios.dog" />

        {/* Open Graph Meta Tags */}
        <meta property="og:title" content="Curios Dog - Ask and Answer Anonymously" />
        <meta property="og:description" content="Curios Dog is coming soon! Join and ask anonymously, reserve your unique username, and stay curious with us!" />
        <meta property="og:image" content="https://curios.dog/curios-dog.jpeg" />
        <meta property="og:url" content={`https://curios.dog/${username || "join"}`} />
        <meta property="og:type" content="website" />

        {/* Twitter Card Meta Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Curios Dog - Ask and Answer Anonymously" />
        <meta name="twitter:description" content="Curios Dog is launching soon! Join the waiting list and reserve your unique username today!" />
        <meta name="twitter:image" content="https://curios.dog/curios-dog.jpeg" />
        <meta name="twitter:url" content={`https://curios.dog/${username || "join"}`} />
      </Helmet>

      <div className="text-center mb-4">
        <Link to="/">
          <img
            src={`${process.env.PUBLIC_URL}/curios-dog.jpeg`}
            alt="Curious Dog"
            className="circular-dog-image" /* Restored dog image */
          />
        </Link>
      </div>

      {/* Form content */}
      <div className="row justify-content-center">
        <div className="col-md-6">
          <h1 className="text-center">
            Join {customPath || "the waiting list"}
          </h1>
          <p className="text-center">
            To reserve your spot, fill out the form below. We'll notify you when
            the site launches!
          </p>

          <form onSubmit={handleSubmit}>
            {errors.general && (
              <div className="alert alert-danger">{errors.general}</div>
            )}
            <div className="mb-3">
              <label htmlFor="username" className="form-label">
                Username (curios.dog/username):
              </label>
              <div className="input-group">
                <span className="input-group-text">curios.dog/</span>
                <input
                  type="text"
                  id="username"
                  className="form-control"
                  value={username}
                  onChange={(e) => setUsername(e.target.value.trim())}
                  required
                  placeholder="your-username"
                />
              </div>
              {errors.username && (
                <small className="text-danger">{errors.username}</small>
              )}
            </div>

            <div className="mb-3">
              <label htmlFor="email" className="form-label">
                Email:
              </label>
              <input
                type="email"
                id="email"
                className="form-control"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
              {errors.email && (
                <small className="text-danger">{errors.email}</small>
              )}
            </div>

            {/* Twitter Handle Input */}
            <div className="mb-3">
              <label htmlFor="twitterHandle" className="form-label">
                X Handle (formerly Twitter):
              </label>
              <input
                type="text"
                id="twitterHandle"
                className="form-control"
                value={twitterHandle}
                onChange={(e) => setTwitterHandle(e.target.value)}
                placeholder="yourhandle"
                pattern="^[a-zA-Z0-9_]{1,15}$" // Validates Twitter handle format
              />
              <small className="form-text text-muted">optional</small>
            </div>

            <div className="consent-container form-check mb-3">
              <input
                type="checkbox"
                id="emailConsent"
                style={{ borderRadius: "2px", width: '25px', border: "2px solid #6c757d" }}
                className="form-check-input"
                checked={emailConsent}
                onChange={(e) => setEmailConsent(e.target.checked)}
              />
              <label htmlFor="emailConsent" className="consent-label">
                I consent to the creation of an account with Curios.dog and
                agree to the <Link to="/terms-of-service">terms and conditions</Link> and {" "}
                <Link to="/privacy-policy">privacy policy</Link>.
              </label>
            </div>

            {/* Error for email consent appears below */}
            {errors.emailConsent && (
              <small className="text-danger d-block mb-3">
                {errors.emailConsent}
              </small>
            )}

            <button
              type="submit"
              className="btn btn-success w-100"
              disabled={isProcessing}
            >
              {isProcessing ? "Processing..." : "Reserve Your Username"}
            </button>
          </form>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default JoinForm;
